"use client";

import React, { useEffect, useState, useCallback, useRef } from "react";
import { usePathname } from "next/navigation";
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap";

import { ArrowClockwise, GeoAlt, Buildings, XLg, ClockHistory } from "react-bootstrap-icons";
import { useField } from "formik";

import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  getProperties,
} from "@/lib/features/home/homeSlice";
import { ROAVE_SEARCH_API_BASE_URL } from "@/config/endpoints";

import styles from "@/components/ui/Loader.module.scss";

interface Location {
  name: string;
  code: string;
  type: string;
  label: string;
  city_code?: string;
}

interface SearchDocument {
  id?: string;
  name: string;
  code: string | number;
  type: string;
  label?: string;
  city_code?: string | number;
  star_category?: number;
}

interface SearchOption {
  score?: number;
  document: SearchDocument;
  isRecent?: boolean;
  isQuickMatch?: boolean;
}

interface LocationSearchProps {
  name: string;
  value: Location;
  setFieldValue: (field: string, value: any) => void;
}

const SEARCH_API_BASE_URL = ROAVE_SEARCH_API_BASE_URL;
const SEARCH_LIMIT = process.env.NEXT_PUBLIC_REDIS_SEARCH_LIMIT ?? "20";
const SEARCH_CACHE_TTL_MS = 300_000;
const SEARCH_DEBOUNCE_MS = 180;
const RECENT_SEARCHES_KEY = "roave_recent_location_searches";
const searchResultsCache = new Map<string, { expiresAt: number; results: SearchOption[] }>();

const QUICK_SEARCH_OPTIONS: SearchOption[] = [
  {
    document: {
      name: "Los Angeles (CA)",
      code: 123656,
      type: "city",
      label: "United States",
    },
  },
  {
    document: {
      name: "Los Cabos",
      code: 990001,
      type: "composite",
      label: "Mexico",
    },
  },
  {
    document: {
      name: "Cancun",
      code: 122105,
      type: "city",
      label: "Mexico",
    },
  },
  {
    document: {
      name: "Puerto Vallarta",
      code: 120780,
      type: "city",
      label: "Mexico",
    },
  },
  {
    document: {
      name: "Puerto Vallarta International Airport",
      code: 700107,
      type: "location",
      label: "Mexico",
    },
  },
  {
    document: {
      name: "New York (NY)",
      code: 120857,
      type: "city",
      label: "United States",
    },
  },
  {
    document: {
      name: "Las Vegas (NV)",
      code: 120955,
      type: "city",
      label: "United States",
    },
  },
  {
    document: {
      name: "San Francisco International Airport",
      code: 724958,
      type: "location",
      label: "San Francisco, United States",
    },
  },
  {
    document: {
      name: "Hard Rock Hotel Los Cabos",
      code: 1588760,
      type: "hotel",
      label: "Cabo San Lucas, Mexico",
      city_code: 122936,
      star_category: 4,
    },
  },
  {
    document: {
      name: "Hyatt Ziva Cancun",
      code: 1589294,
      type: "hotel",
      label: "Cancun, Mexico",
      city_code: 122105,
      star_category: 4,
    },
  },
];

const searchCacheKey = (query: string) => `${query.trim().toLowerCase()}|${SEARCH_LIMIT}`;

const getCachedSearchResults = (query: string) => {
  const cached = searchResultsCache.get(searchCacheKey(query));
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    searchResultsCache.delete(searchCacheKey(query));
    return null;
  }
  return cached.results;
};

const setCachedSearchResults = (query: string, results: SearchOption[]) => {
  if (results.length === 0) return;

  if (searchResultsCache.size > 100) {
    const firstKey = searchResultsCache.keys().next().value;
    if (firstKey) searchResultsCache.delete(firstKey);
  }

  searchResultsCache.set(searchCacheKey(query), {
    expiresAt: Date.now() + SEARCH_CACHE_TTL_MS,
    results,
  });
};

const resultKey = (option: SearchOption) =>
  `${option.document.type}:${option.document.code || option.document.id || option.document.name}`;

const dedupeOptions = (options: SearchOption[]) => {
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = resultKey(option);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeForMatch = (value = "") => value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const getQuickMatches = (query: string) => {
  const normalizedQuery = normalizeForMatch(query);
  if (!normalizedQuery) return QUICK_SEARCH_OPTIONS.slice(0, 5);
  const words = normalizedQuery.split(" ").filter(Boolean);

  return QUICK_SEARCH_OPTIONS
    .filter((option) => {
      const haystack = normalizeForMatch(`${option.document.name} ${option.document.label ?? ""}`);
      return words.every((word) => haystack.includes(word)) || haystack.startsWith(normalizedQuery);
    })
    .map((option) => ({ ...option, isQuickMatch: true }))
    .slice(0, 5);
};

const loadRecentSearches = (): SearchOption[] => {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((document): document is SearchDocument => Boolean(document?.name && document?.code && document?.type))
      .map((document) => ({ document, isRecent: true }));
  } catch {
    return [];
  }
};

const saveRecentSearch = (document: SearchDocument) => {
  if (typeof window === "undefined") return;
  const recentDocuments = dedupeOptions([{ document }, ...loadRecentSearches()])
    .slice(0, 5)
    .map((option) => option.document);
  window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentDocuments));
};

const groupOptions = (options: SearchOption[]) => {
  const destinations = options.filter((item) => item.document.type !== "hotel");
  const hotels = options.filter((item) => item.document.type === "hotel");
  return { destinations, hotels };
};

const LocationSearch = ({
  name,
  value,
  setFieldValue,
}: LocationSearchProps) => {
  const dispatch = useAppDispatch();
  const [, meta] = useField(name);
  const pathname = usePathname();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const searchRequestIdRef = useRef(0);

  const [loading, setLoading] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState<Location | null>(null);
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [recentOptions, setRecentOptions] = useState<SearchOption[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRecentOptions(loadRecentSearches());
  }, []);
  /* ----------------------------------
   * Sync external Formik value
   * ---------------------------------- */
  useEffect(() => {
    if (!value?.code) return;

    handleSelect(value);

    if (value.type !== "hotel" && pathname === "/hotels") {
      dispatch(
        getProperties({
          apiUrl: ROAVE_SEARCH_API_BASE_URL,
          url: "api/app/properties",
          payload: {
            q: "",
            city_code: value.code,
            type: value.type,
            page: 1,
            limit: 1000,
          },
        })
      );
    }
  }, [value, pathname]);

  /* ----------------------------------
   * Debounce
   * ---------------------------------- */
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const triggerSearch = useCallback(async (query: string) => {
    const normalizedQuery = query.trim();
    const cachedResults = getCachedSearchResults(normalizedQuery);
    if (cachedResults) {
      setOptions(cachedResults);
      setLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = `query=${encodeURIComponent(normalizedQuery)}&limit=${SEARCH_LIMIT}`;
      let res: Response;
      try {
        res = await fetch(`${SEARCH_API_BASE_URL}/api/app/autocomplete?${params}`, {
          signal: controller.signal,
          cache: "no-store",
        });
      } catch (directError: any) {
        if (directError?.name === "AbortError") throw directError;
        res = await fetch(`/api/app/autocomplete?${params}`, {
          signal: controller.signal,
          cache: "no-store",
        });
      }
      if (!res.ok) {
        res = await fetch(`/api/app/autocomplete?${params}`, {
          signal: controller.signal,
          cache: "no-store",
        });
      }
      if (!res.ok) throw new Error("search error");
      const json = await res.json();
      const results = dedupeOptions(json?.data?.data?.results ?? []);
      if (searchRequestIdRef.current !== requestId) return;
      setCachedSearchResults(normalizedQuery, results);
      setOptions(results);
    } catch (e: any) {
      if (searchRequestIdRef.current === requestId && e?.name !== "AbortError") setOptions([]);
    } finally {
      if (searchRequestIdRef.current === requestId) setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => triggerSearch(query), SEARCH_DEBOUNCE_MS);
    },
    [triggerSearch]
  );

  //Control dropdown open ONLY here
  useEffect(() => {
    setDropdownOpen(isInputFocused);
  }, [isInputFocused]);

  /* ----------------------------------
   * Handlers
   * ---------------------------------- */
  const handleInputChange = (value: string) => {
    setSearchValue(value);

    if (value.length >= 2) {
      const cachedResults = getCachedSearchResults(value);
      if (cachedResults) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setOptions(cachedResults);
        setLoading(false);
      } else {
        const quickMatches = getQuickMatches(value);
        setOptions(quickMatches);
        setLoading(true);
        debouncedSearch(value);
      }
    } else {
      setOptions([]);
      setLoading(false);
      searchRequestIdRef.current += 1;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    }
  };

  const handleSelect = useCallback(
    (item: SearchDocument) => {
      setSearchValue(item.name);
      setSelectedItem(item as Location);
      setFieldValue(name, item);
      saveRecentSearch(item);
      setRecentOptions(loadRecentSearches());
      setIsInputFocused(false);
      setDropdownOpen(false);
    },
    [name, setFieldValue]
  );
  
  const handleClear = () => {
    setSearchValue("");
    setSelectedItem(null);
    setOptions([]);
    setLoading(false);
    searchRequestIdRef.current += 1;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    setIsInputFocused(false);
    setDropdownOpen(false);
    setFieldValue(name, {});
  };

  //Click-outside effect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsInputFocused(false);
        setDropdownOpen(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  
 

  const queryHasSearchText = searchValue.trim().length >= 2;
  const defaultOptions = recentOptions.length > 0 ? recentOptions : getQuickMatches("");
  const visibleOptions = queryHasSearchText ? options : defaultOptions;
  const { destinations, hotels } = groupOptions(visibleOptions);

  const renderSectionHeader = (label: string) => (
    <li className="location-search-section" role="presentation">
      {label}
    </li>
  );

  const renderOption = (item: SearchOption, index: number) => {
    const doc = item.document;
    const Icon = item.isRecent ? ClockHistory : doc.type === "hotel" ? Buildings : GeoAlt;

    return (
      <li
        key={`${resultKey(item)}-${index}`}
        className={`-link d-block col-12 text-left borderradius-40 px-20 py-15 sm:py-10 js-search-option mb-1 ${
          String(selectedItem?.code) === String(doc.code) ? "active" : ""
        } sm:text-12`}
        role="option"
        aria-selected={String(selectedItem?.code) === String(doc.code)}
        onClick={() => handleSelect(doc)}
      >
        <div className="d-flex">
          <Icon className="text-light-1 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
          <div className="ml-10">
            <div className="text-15 sm:text-14 lh-12 location-search-option-name">{doc.name}</div>
            <div className="text-14 sm:text-12 lh-12 text-light-1 mt-5">
              {doc.label || (doc.type === "hotel" ? "Hotel" : "Destination")}
            </div>
          </div>
        </div>
      </li>
    );
  };

  /* ----------------------------------
   * Render
   * ---------------------------------- */
  return (
    <div className="searchMenu-loc px-30 lg:px-0" ref={containerRef} >
      <Dropdown isOpen={dropdownOpen} >
        <DropdownToggle tag="div" className="dropdown-toggle" role="button">
          <h4 className="text-15 fw-500">Location</h4>

          <div className="d-flex align-items-center">
            <input
              ref={inputRef}
              type="search"
              placeholder="Where are you going?"
              autoComplete="off"
              value={searchValue}
              onFocus={() => setIsInputFocused(true)}
              onChange={(e) => handleInputChange(e.target.value)}
              className={`js-search ${
                meta?.touched && meta?.error ? "input-bottom-error" : ""
              }`}
              style={{ flex: 1 }}
              aria-haspopup="listbox"
              aria-controls="location-search-options"
              aria-label="Search location"
              // disabled={hotelLoading}
              tabIndex={-1}
            />

            {searchValue && (
              <XLg
                title="Reset"
                className="clear-icon ml-2"
                onClick={handleClear}
                size={15}
                aria-label="Clear search input"
              />
            )}

            {loading && (
              <ArrowClockwise
                className={styles.spinner}
                size={15}
                aria-hidden="true"
              />
            )}
          </div>
        </DropdownToggle>

        <DropdownMenu
          className="shadow-2 min-width-400 searchMenu-loc__search-bar-dropdown"
          end
          autoclose="outside"
          role="listbox"
        >
          <div className="bg-white px-20 py-20 sm:px-0 sm:py-0 borderradius-40 scrollable-container location-search-panel">
            <ul className="y-gap-5 js-results mb-0" id="location-search-options">
              {!queryHasSearchText && recentOptions.length > 0 && renderSectionHeader("Recent searches")}
              {!queryHasSearchText && recentOptions.length === 0 && renderSectionHeader("Popular searches")}

              {queryHasSearchText && destinations.length > 0 && renderSectionHeader("Destinations")}
              {destinations.map(renderOption)}

              {queryHasSearchText && hotels.length > 0 && renderSectionHeader("Hotels")}
              {hotels.map((item, index) => renderOption(item, destinations.length + index))}

              {queryHasSearchText && loading && (
                <li className="location-search-status px-20 py-15" role="status">
                  <ArrowClockwise className={styles.spinner} size={15} aria-hidden="true" />
                  <span className="ml-10">Searching for "{searchValue.trim()}"</span>
                </li>
              )}

              {queryHasSearchText && !loading && visibleOptions.length === 0 && (
                <li className="location-search-status px-20 py-15" role="status">
                  No matches found
                </li>
              )}
            </ul>
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default React.memo(LocationSearch);
